import { Chip, SvgIcon, ThemeOptions } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { capitalDesignTokens } from '../../capital_design_tokens';
import { getListItemDisabledStyles } from '../common/MuiListItemButton';
import {
  DEFAULT_CHIP_LABEL_PADDING,
  getChipColors,
  getChipRootStyles,
  getChipSizeStyles
} from '../dataDisplay/MuiChip';
import { getMenuPaperStyle } from '../dataDisplay/MuiMenu';

// tslint:disable:interface-name
declare module '@mui/material/Autocomplete' {
  export interface AutocompletePropsSizeOverrides {
    large: true;
  }
  export interface AutocompleteClasses {
    tagSizeLarge: true;
  }
}

export const MuiAutocomplete: ThemeOptions['components']['MuiAutocomplete'] = {
  defaultProps: {
    autoHighlight: true,
    ChipProps: {
      variant: 'outlined',
      deleteIcon: <CloseIcon />
    },
    getLimitTagsText: (more) => (
      <Chip
        className="cds-autocomplete-limitTag"
        label={`+ ${more} more`}
        variant="outlined"
        component="span"
        sx={{ display: 'flex' }}
      />
    ),
    limitTags: 2,
    renderOption(props, option, state, ownerState) {
      const isMultipleSelectedOption = state.selected && ownerState.multiple;
      props.className = `${props.className}${isMultipleSelectedOption ? ' cds-autocomplete-multiple-selectedOption' : ''
        }`;
      return (
        <li {...props}>
          {isMultipleSelectedOption && <SvgIcon component={CheckIcon} inheritViewBox sx={{ marginRight: 1 }} />}
          {ownerState.getOptionLabel(option)}
        </li>
      );
    }
  },

  styleOverrides: {
    root: ({ theme }) => {
      const chipSizeStyles = getChipSizeStyles(theme);
      const outlinedChipColors = getChipColors('default', 'outlined', theme);
      return {
        /**
         * Since Autocomplete components are typically composed with a TextField/Input component,
         * we need to override the margin positioning defined for end adornment buttons in MuiInput
         * in order to get correct positioning for dropdown arrow and clear button.
         */
        '.MuiAutocomplete-endAdornment .MuiButtonBase-root': {
          margin: 'initial',
          marginRight: theme.spacing(-0.25)
        },
        '.MuiAutocomplete-clearIndicator': {
          margin: 'initial'
        },

        // Autocomplete input root container
        '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,

          // Autocomplete input field — uses height: 100% + box-sizing: border-box from
          // MuiOutlinedInput.input, so padding here is included within the container height.
          // vertical padding = (containerHeight - lineHeight) / 2
          '& .MuiAutocomplete-input': {
            fontSize: capitalDesignTokens.componentFontSizes.medium,
            lineHeight: '20px',
            padding: theme.spacing(1, 1.5)   // 8px top/bottom: (36 - 20) / 2 = 8px ✓
          },
          // Then override with size-specific styles
          '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
            fontSize: capitalDesignTokens.componentFontSizes.small,
            lineHeight: '16px',
            padding: theme.spacing(1, 1.5)   // 8px top/bottom: (32 - 16) / 2 = 8px ✓
          },
          '&.MuiInputBase-sizeLarge .MuiAutocomplete-input': {
            fontSize: capitalDesignTokens.componentFontSizes.large,
            lineHeight: '20px',
            padding: theme.spacing(1.5, 2)   // 12px top/bottom: (44 - 20) / 2 = 12px ✓
          }
        },

        /* Workaround to ensure theming styles are applied to the limit tag in hosted environment.
         * Because this Chip component is added through the defaultProps above,
         * our MuiChip theming is not applied to it in hosted environments.
         */
        '& .cds-autocomplete-limitTag': {
          ...getChipRootStyles(outlinedChipColors),
          borderColor: outlinedChipColors.borderColor,
          fontFamily: capitalDesignTokens.foundations.typography.baseFont,
          fontFeatureSettings: "'ss03' on",
          '& .MuiChip-label': {
            px: DEFAULT_CHIP_LABEL_PADDING
          }
        },
        // Multiselect limit tag indicator
        '.MuiAutocomplete-tag .cds-autocomplete-limitTag': {
          ...chipSizeStyles.small // Default (medium) size
        },
        '.MuiAutocomplete-tagSizeSmall .cds-autocomplete-limitTag': {
          ...chipSizeStyles.extraSmall
        },
        '.MuiAutocomplete-tagSizeLarge .cds-autocomplete-limitTag': {
          ...chipSizeStyles.medium
        }
      };
    },

    // Option list container
    paper: ({ theme }) => ({
      ...getMenuPaperStyle(theme)
    }),
    listbox: ({ theme }) => ({
      '.MuiAutocomplete-option': {
        fontSize: capitalDesignTokens.componentFontSizes.medium,
        lineHeight: '20px',

        '&[aria-disabled="true"]': {
          ...getListItemDisabledStyles(theme)
        }
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            '.MuiAutocomplete-option': {
              fontSize: capitalDesignTokens.componentFontSizes.small,
              lineHeight: '16px'
            }
          }
        },
        {
          props: { size: 'large' },
          style: {
            '.MuiAutocomplete-option': {
              fontSize: capitalDesignTokens.componentFontSizes.large,
              lineHeight: '32px'
            }
          }
        },
        {
          props: { multiple: true },
          style: {
            '.MuiAutocomplete-option': {
              paddingLeft: '40px', // theme.spacing(5)
              '&.cds-autocomplete-multiple-selectedOption': {
                paddingLeft: '8px' // theme.spacing(1)
              },
              '&[aria-selected="true"]': {
                background: 'none'
              }
            }
          }
        }
      ]
    }),

    // Multiselect tag spacing and sizing
    tag: ({ theme }) => ({
      '&, &.MuiButtonBase-root': {
        margin: theme.spacing(0, 0.5, 0, 0)
      }
    }),
    tagSizeSmall: ({ theme }) => ({
      '&, &.MuiButtonBase-root': {
        ...getChipSizeStyles(theme).extraSmall,
        margin: theme.spacing(0.25, 0.75, 0.25, 0.25)
      }
    }),
    tagSizeMedium: ({ theme }) => ({
      '&, &.MuiButtonBase-root': {
        ...getChipSizeStyles(theme).small,
        margin: theme.spacing(0.5)
      }
    }),
    tagSizeLarge: ({ theme }) => ({
      '&, &.MuiButtonBase-root': {
        ...getChipSizeStyles(theme).medium,
        margin: theme.spacing(0.5)
      }
    })
  }
};
